import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IssueBoardStatusEnums } from "../issueConstants";

type MultiRegisterIssuesTaskOrIssueFilter = "ISSUE" | "TASK" | null;
export type MultiRegisterIssuesSortField =
  | "issueBoard.name"
  | "id"
  | "title"
  | "status"
  | "assignedTo"
  | "dates.due"
  | "pns";

export interface IMultiRegisterIssuesSort {
  field: MultiRegisterIssuesSortField;
  direction: "asc" | "desc";
}

export interface IMultiRegisterIssuesFilters {
  taskOrIssue: MultiRegisterIssuesTaskOrIssueFilter;
  freeText: string;
  issueboardIds: string[] | null;
  status: string[];
  assignedToIds: string[];
}

export interface IMultiRegisterIssuesReducer {
  selectedIssueBoardIds: string[];
  issueBoardIdsFromUrl: string[];
  hasSyncedIssueBoardIdsFromUrl: boolean;
  issueBoardsTableActiveStatus: IssueBoardStatusEnums;
  page: number;
  pageSize: number;
  sort: IMultiRegisterIssuesSort | null;
  filters: IMultiRegisterIssuesFilters;
}

type PersistedMultiRegisterIssuesState = Pick<IMultiRegisterIssuesReducer, "page" | "pageSize" | "sort" | "filters">;

const MULTI_REGISTER_ISSUES_LOCAL_STORAGE_KEY = "multiRegisterIssuesState";
const MULTI_REGISTER_SORT_FIELDS: MultiRegisterIssuesSortField[] = [
  "issueBoard.name",
  "id",
  "title",
  "status",
  "assignedTo",
  "dates.due",
  "pns",
];

const defaultPersistedMultiRegisterIssuesState: PersistedMultiRegisterIssuesState = {
  page: 1,
  pageSize: 500,
  sort: { field: "id", direction: "asc" },
  filters: {
    taskOrIssue: null,
    freeText: "",
    issueboardIds: null,
    status: [],
    assignedToIds: [],
  },
};

const isValidSort = (sort: unknown): sort is IMultiRegisterIssuesSort => {
  if (sort == null || typeof sort !== "object") return false;
  const candidate = sort as IMultiRegisterIssuesSort;
  return (
    MULTI_REGISTER_SORT_FIELDS.includes(candidate.field) &&
    (candidate.direction === "asc" || candidate.direction === "desc")
  );
};

const readPersistedMultiRegisterIssuesState = (): PersistedMultiRegisterIssuesState => {
  try {
    const persisted = JSON.parse(
      localStorage.getItem(MULTI_REGISTER_ISSUES_LOCAL_STORAGE_KEY) ?? "null",
    ) as Partial<PersistedMultiRegisterIssuesState> | null;

    if (persisted == null) return defaultPersistedMultiRegisterIssuesState;

    return {
      page:
        typeof persisted.page === "number" && persisted.page > 0
          ? persisted.page
          : defaultPersistedMultiRegisterIssuesState.page,
      pageSize:
        typeof persisted.pageSize === "number" && persisted.pageSize > 0
          ? persisted.pageSize
          : defaultPersistedMultiRegisterIssuesState.pageSize,
      sort:
        persisted.sort == null
          ? null
          : isValidSort(persisted.sort)
          ? persisted.sort
          : defaultPersistedMultiRegisterIssuesState.sort,
      filters: {
        taskOrIssue:
          persisted.filters?.taskOrIssue === "ISSUE" || persisted.filters?.taskOrIssue === "TASK"
            ? persisted.filters.taskOrIssue
            : null,
        freeText: typeof persisted.filters?.freeText === "string" ? persisted.filters.freeText : "",
        issueboardIds: Array.isArray(persisted.filters?.issueboardIds) ? persisted.filters.issueboardIds : null,
        status: Array.isArray(persisted.filters?.status) ? persisted.filters.status : [],
        assignedToIds: Array.isArray(persisted.filters?.assignedToIds) ? persisted.filters.assignedToIds : [],
      },
    };
  } catch {
    return defaultPersistedMultiRegisterIssuesState;
  }
};

const persistMultiRegisterIssuesState = (state: IMultiRegisterIssuesReducer) => {
  try {
    localStorage.setItem(
      MULTI_REGISTER_ISSUES_LOCAL_STORAGE_KEY,
      JSON.stringify({
        page: state.page,
        pageSize: state.pageSize,
        sort: state.sort,
        filters: state.filters,
      } satisfies PersistedMultiRegisterIssuesState),
    );
  } catch {
    // ignore storage errors
  }
};

const persistedInitialState = readPersistedMultiRegisterIssuesState();

const initialState: IMultiRegisterIssuesReducer = {
  selectedIssueBoardIds: [],
  issueBoardIdsFromUrl: [],
  hasSyncedIssueBoardIdsFromUrl: false,
  issueBoardsTableActiveStatus: IssueBoardStatusEnums.ACTIVE,
  page: persistedInitialState.page,
  pageSize: persistedInitialState.pageSize,
  sort: persistedInitialState.sort,
  filters: persistedInitialState.filters,
};

export const multiRegisterIssuesSlice = createSlice({
  name: "multiRegisterIssues",
  initialState,
  reducers: {
    setSelectedMultiRegisterIssueBoardIdsInRedux: (state, action: PayloadAction<string[]>) => {
      state.selectedIssueBoardIds = action.payload;
    },
    setMultiRegisterIssueBoardIdsFromUrlInRedux: (state, action: PayloadAction<string[]>) => {
      const shouldResetIssueBoardFilter = state.hasSyncedIssueBoardIdsFromUrl;
      state.issueBoardIdsFromUrl = action.payload;
      state.hasSyncedIssueBoardIdsFromUrl = true;
      if (shouldResetIssueBoardFilter) {
        state.page = 1;
        state.filters.issueboardIds = null;
      }
    },
    toggleSelectedMultiRegisterIssueBoardIdInRedux: (state, action: PayloadAction<string>) => {
      const issueBoardId = action.payload;
      if (state.selectedIssueBoardIds.includes(issueBoardId)) {
        state.selectedIssueBoardIds = state.selectedIssueBoardIds.filter(
          (selectedIssueBoardId) => selectedIssueBoardId !== issueBoardId,
        );
      } else {
        state.selectedIssueBoardIds.push(issueBoardId);
      }
    },
    clearSelectedMultiRegisterIssueBoardIdsInRedux: (state) => {
      state.selectedIssueBoardIds = [];
    },
    setIssueBoardsTableActiveStatusInRedux: (state, action: PayloadAction<IssueBoardStatusEnums>) => {
      state.issueBoardsTableActiveStatus = action.payload;
    },
    setMultiRegisterIssuesPageInRedux: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
      persistMultiRegisterIssuesState(state);
    },
    setMultiRegisterIssuesPageSizeInRedux: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 1;
      persistMultiRegisterIssuesState(state);
    },
    setMultiRegisterIssuesTaskOrIssueFilterInRedux: (
      state,
      action: PayloadAction<MultiRegisterIssuesTaskOrIssueFilter>,
    ) => {
      state.filters.taskOrIssue = action.payload;
      state.page = 1;
      persistMultiRegisterIssuesState(state);
    },
    setMultiRegisterIssuesFreeTextFilterInRedux: (state, action: PayloadAction<string>) => {
      if (state.filters.freeText === action.payload) return;
      state.filters.freeText = action.payload;
      state.page = 1;
      persistMultiRegisterIssuesState(state);
    },
    setMultiRegisterIssuesIssueBoardIdsFilterInRedux: (state, action: PayloadAction<string[] | null>) => {
      state.filters.issueboardIds = action.payload;
      state.page = 1;
      persistMultiRegisterIssuesState(state);
    },
    setMultiRegisterIssuesStatusFilterInRedux: (state, action: PayloadAction<string[]>) => {
      state.filters.status = action.payload;
      state.page = 1;
      persistMultiRegisterIssuesState(state);
    },
    setMultiRegisterIssuesAssignedToFilterInRedux: (state, action: PayloadAction<string[]>) => {
      state.filters.assignedToIds = action.payload;
      state.page = 1;
      persistMultiRegisterIssuesState(state);
    },
    setMultiRegisterIssuesSortInRedux: (state, action: PayloadAction<IMultiRegisterIssuesSort | null>) => {
      state.sort = action.payload;
      state.page = 1;
      persistMultiRegisterIssuesState(state);
    },
  },
});

export const {
  setSelectedMultiRegisterIssueBoardIdsInRedux,
  setMultiRegisterIssueBoardIdsFromUrlInRedux,
  toggleSelectedMultiRegisterIssueBoardIdInRedux,
  clearSelectedMultiRegisterIssueBoardIdsInRedux,
  setIssueBoardsTableActiveStatusInRedux,
  setMultiRegisterIssuesPageInRedux,
  setMultiRegisterIssuesPageSizeInRedux,
  setMultiRegisterIssuesTaskOrIssueFilterInRedux,
  setMultiRegisterIssuesFreeTextFilterInRedux,
  setMultiRegisterIssuesIssueBoardIdsFilterInRedux,
  setMultiRegisterIssuesStatusFilterInRedux,
  setMultiRegisterIssuesAssignedToFilterInRedux,
  setMultiRegisterIssuesSortInRedux,
} = multiRegisterIssuesSlice.actions;

export default multiRegisterIssuesSlice.reducer;
